import React, { useState } from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { DocumentNode, useMutation } from '@apollo/client';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { Field, Textarea, Select, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { THIRD_PARTY } from '../../../../app/common/constants/SiteConstants';
import { IFilingInfo } from '../../serviceContacts/interfaces';
import ActionButtons from '../../../common/ActionButtons';
import LoadingErrorMessage from '../../../common/errorContent/LoadingErrorMessage';
import validateRequiredFilingInfo from '../../fiscalYears/validators/FilingInfoValidator';
import { CoherencePanelStyles } from '../../../../app/common/styles/CommonStyleObjects';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import OversightGroupDropdown from '../../fiscalYears/OversightGroupDropdown';

interface IFilingInfoFormProps {
  closePanel: () => void;
  filingInfoData: IFilingInfo;
  mutation: DocumentNode;
  parentId: string | number;
  parentIdName: string;
}

const FilingInfoForm = (props: IFilingInfoFormProps): JSX.Element => {
  const { closePanel, filingInfoData, mutation, parentId, parentIdName } = props;
  const [pageState, setPageState] = useState({ ...filingInfoData });

  const [updateFilingInfo, { loading, error }] = useMutation(mutation, {
    onCompleted: () => {
      closePanel();
    },
  });

  const onOverSightGroupChange = (newValue: IDropdownReference, valueKey: string): void => {
    setPageState({ ...pageState, oversightGroup: newValue });
  };

  const onResponsibleForStatFilingPreparationChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const selectedValue = event.target.value;
    if (selectedValue !== THIRD_PARTY) {
      setPageState({
        ...pageState,
        responsibleForStatFilingPreparation: selectedValue,
        responsibleForStatFilingPreparationThirdParty: null,
      });
    } else {
      setPageState({
        ...pageState,
        responsibleForStatFilingPreparation: selectedValue,
      });
    }
  };

  const onResponsibleForRecordChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const selectedValue = event.target.value;
    if (selectedValue !== THIRD_PARTY) {
      setPageState({
        ...pageState,
        responsibleForRecord: selectedValue,
        responsibleForRecordThirdParty: null,
      });
    } else {
      setPageState({ ...pageState, responsibleForRecord: selectedValue });
    }
  };

  const onResponsibleForStatFilingPreparationThirdPartyChange = (newValue?: string): void => {
    setPageState({ ...pageState, responsibleForStatFilingPreparationThirdParty: newValue });
  };

  const onResponsibleForRecordChangeThirdParty = (newValue?: string): void => {
    setPageState({ ...pageState, responsibleForRecordThirdParty: newValue });
  };

  const onOtherInfoChange = (newValue?: string): void => {
    setPageState({ ...pageState, otherInformation: newValue });
  };

  const validateForm = (): void => {
    const errors = validateRequiredFilingInfo(pageState);

    if (!Object.keys(errors).length) {
      const payload = {
        id: pageState.id || -1,
        oversightGroup: pageState.oversightGroup
          ? {
              id: pageState.oversightGroup?.id,
              fieldName: pageState.oversightGroup?.fieldName,
            }
          : null,
        responsibleForStatFilingPreparation: pageState.responsibleForStatFilingPreparation,
        responsibleForStatFilingPreparationThirdParty:
          pageState.responsibleForStatFilingPreparationThirdParty || '',
        responsibleForRecord: pageState.responsibleForRecord,
        responsibleForRecordThirdParty: pageState.responsibleForRecordThirdParty || '',
        otherInformation: pageState.otherInformation || '',
      };

      updateFilingInfo({
        variables: {
          [parentIdName]: parentId,
          filingInfo: payload,
        },
      });
    }

    setPageState({ ...pageState, errors });
  };

  return (
    <CoherencePanel
      closeButtonAriaLabel="Close Filing Info Edit Panel"
      panelSize={CoherencePanelSize.medium}
      titleText="Edit Filing Information"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel="Save Filing Information"
          saveTitle="Save Filing Information"
          cancelLabel="Cancel Filing Information Edit"
          cancelTitle="Cancel Filing Information Edit"
          handleSubmit={validateForm}
          mutationLoading={loading}
        />
      )}
    >
      <LoadingErrorMessage loading={loading} error={error} />
      <form>
        <FluentProvider theme={webLightTheme}>
          <Stack>
            <OversightGroupDropdown
              label="Oversight Group"
              valueKey="oversightGroup"
              required
              errors={pageState?.errors}
              handleChange={onOverSightGroupChange}
              value={pageState.oversightGroup}
            />
            <StackItem>
              <Field
                label="Responsible for STAT Filing Preparation"
                required
                validationMessage={pageState?.errors?.responsibleForStatFilingPreparation?.message}
              >
                <Select
                  value={pageState?.responsibleForStatFilingPreparation || ''}
                  onChange={onResponsibleForStatFilingPreparationChange}
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  <option value="BPO">BPO</option>
                  <option value="DAVA">DAVA</option>
                  <option value="ONEFINANCE">One Finance</option>
                  <option value="ROCCOE">ROC COE</option>
                  <option value="STATUTORYCONTROLLER">Statutory Controller</option>
                  <option value={THIRD_PARTY}>Third Party</option>
                  <option value="NOFILINGREQUIREMENT">No Filing Required</option>
                  <option value="ENTITYTERMINATED">Entity Terminated</option>
                </Select>
              </Field>
              <br />
              <Field
                validationMessage={
                  pageState?.errors?.responsibleForStatFilingPreparationThirdParty?.message
                }
              >
                <Textarea
                  aria-label="Name of Third Party"
                  placeholder="Name of Third Party"
                  rows={3}
                  disabled={pageState.responsibleForStatFilingPreparation !== THIRD_PARTY}
                  resize="none"
                  value={pageState.responsibleForStatFilingPreparationThirdParty || ''}
                  onChange={(e, data) =>
                    onResponsibleForStatFilingPreparationThirdPartyChange(data.value)
                  }
                />
              </Field>
            </StackItem>
            <StackItem>
              <Field
                label="Responsible for Record"
                required
                validationMessage={pageState?.errors?.responsibleForRecord?.message}
              >
                <Select
                  value={pageState?.responsibleForRecord || ''}
                  onChange={onResponsibleForRecordChange}
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  <option value="BPO">BPO</option>
                  <option value="CORP">Corp</option>
                  <option value="DAVA">DAVA</option>
                  <option value="LOCALFINANCE">Local Finance</option>
                  <option value={THIRD_PARTY}>Third Party</option>
                  <option value="NOFILINGREQUIREMENT">No Filing Required</option>
                  <option value="ENTITYTERMINATED">Entity Terminated</option>
                </Select>
              </Field>
              <br />
              <Field validationMessage={pageState?.errors?.responsibleForRecordThirdParty?.message}>
                <Textarea
                  aria-label="Name of Third Party"
                  placeholder="Name of Third Party"
                  rows={3}
                  disabled={pageState.responsibleForRecord !== THIRD_PARTY}
                  resize="none"
                  value={pageState.responsibleForRecordThirdParty || ''}
                  onChange={(e, data) => onResponsibleForRecordChangeThirdParty(data.value)}
                />
              </Field>
            </StackItem>
            <Field label="Other Information">
              <Textarea
                aria-label="Other Information"
                resize="none"
                value={pageState?.otherInformation || ''}
                onChange={(e, data) => onOtherInfoChange(data.value)}
              />
            </Field>
          </Stack>
        </FluentProvider>
      </form>
    </CoherencePanel>
  );
};

export default FilingInfoForm;
